export type Order = {
  id: string;
  estimation_number: string;
  project_name: string;
  customer_name: string;
  pic: string;
  pic_ruby: string;
  phone_number: string;
  created_at: string;
  is_confirmed?: boolean;
  unread_comment_count?: number;
};

export type Ticket = {
  id: string;
  drawing_id: string;
  progress: number;
  subcontractor_id: string;
  subcontractor_name: string;
  status: number;
  previous_status: number;
  created_at: string;
  delivery_at: string;
  file_name: string;
  deadline_alert_sent: number | null;
  s3_url_image: string;
  amount?: number | null;
  is_overdue?: boolean;
};

export type DataOrder = {
  order: Order;
  tickets: Ticket[];
};

export type FormData = {
  username: string;
  email: string;
  password: string;
  fullname: string;
  phone_number: string;
  confirmPassword: string;
  type: string;
  [key: string]: string | "";
};
export type FormEditData = {
  username: string;
  email: string;
  fullname: string;
  phone_number: string;
  [key: string]: string | "";
};

// Supported CAD file extensions for rakuCADtrace
export const SUPPORTED_CAD_EXTENSIONS = [
  '.dwg', '.dxf',           // AutoCAD
  '.vwx',                    // Vectorworks
  '.jww', '.jwc',           // Jw_cad
  '.sfc', '.p21', '.sfz', '.p2z', // SXF
  '.bfo',                    // V-nas
  '.tfv', '.dvv', '.dvm',   // CADWe'll土木
  '.bvf',                    // BV FILE
  '.zrd', '.zfd', '.zsd', '.zsdx' // 図脳RAPID
];

export const CAD_FILE_ACCEPT_STRING = ".dwg,.dxf,.vwx,.jww,.jwc,.sfc,.p21,.sfz,.p2z,.bfo,.tfv,.dvv,.dvm,.bvf,.zrd,.zfd,.zsd,.zsdx";

export const CAD_FILE_EXTENSIONS_DISPLAY = "DWG, DXF, VWX, JWW, JWC, SFC, P21, SFZ, P2Z, BFO, TFV, DVV, DVM, BVF, ZRD, ZFD, ZSD, ZSDX";

export const TicketStatus = [
  "CUSTOMER_CREATED",
  "CUSTOMER_ORDERED",
  "COMPANY_ORDER_RECEIVED",
  "COMPANY_ASSIGNED",
  "COMPANY_ORDER_REJECTED",
  "COMPANY_DELIVERY_LATE",
  "SUB_ORDER_ACCEPTED",
  "SUB_INPROGRESS",
  "SUB_DELIVERED",
  "SUB_ORDER_REJECTED",
  "SUB_DELIVERY_LATE",
  "COMPANY_INSPECTION_ONGOING",
  "COMPANY_INSPECTION_COMPLETED",
  "COMPANY_DELIVERED",
  "COMPANY_REDO_REQUESTED",
  "CUSTOMER_INSPECTION_ONGOING",
  "CUSTOMER_INSPECTION_COMPLETED",
  "CUSTOMER_DELIVERY_REJECTED",
];

// 顧客向けステータス3軸表示（設計書02「2. ステータスマッピング（確定版）」準拠）
export type CustomerStatusColor = "yellow" | "blue" | "green" | "red";

export interface CustomerStatusDisplay {
  label: string; // 状態バッジ
  nextAction: string; // 次アクション（丁寧体）
  owner: "弊社" | "お客様" | ""; // 担当
  color: CustomerStatusColor;
}

// 見積不可（設計書01の擬似ステータス）用の表示定義。
// TicketStatus enum には存在しないため、専用キーで参照する。
export const FAILED_ESTIMATE_STATUS_KEY = "FAILED_ESTIMATE";

export const customerStatusDisplay: Record<string, CustomerStatusDisplay> = {
  CUSTOMER_CREATED: {
    label: "見積完了（未発注）",
    nextAction: "発注内容をご確認のうえ発注確定してください",
    owner: "お客様",
    color: "yellow",
  },
  [FAILED_ESTIMATE_STATUS_KEY]: {
    label: "見積不可",
    nextAction: "お見積フォームよりご依頼ください",
    owner: "お客様",
    color: "red",
  },
  CUSTOMER_ORDERED: {
    label: "発注確定済み",
    nextAction: "弊社の受注確認をお待ちください",
    owner: "弊社",
    color: "blue",
  },
  COMPANY_ORDER_RECEIVED: {
    label: "受注確定済み",
    nextAction: "弊社にて作業準備中です",
    owner: "弊社",
    color: "blue",
  },
  COMPANY_ASSIGNED: {
    label: "作業準備中",
    nextAction: "作業開始までお待ちください",
    owner: "弊社",
    color: "blue",
  },
  COMPANY_ORDER_REJECTED: {
    label: "受注不可",
    nextAction: "お手数ですがお問い合わせフォームよりご連絡ください",
    owner: "お客様",
    color: "red",
  },
  // 期限超過系（5, 10）は顧客には通常の「作業中」を表示（開示しない）
  COMPANY_DELIVERY_LATE: {
    label: "作業中",
    nextAction: "完了までお待ちください",
    owner: "弊社",
    color: "blue",
  },
  SUB_ORDER_ACCEPTED: {
    label: "作業中（着手済）",
    nextAction: "作業着手済みです",
    owner: "弊社",
    color: "blue",
  },
  SUB_INPROGRESS: {
    label: "作業中",
    nextAction: "完了までお待ちください",
    owner: "弊社",
    color: "blue",
  },
  SUB_DELIVERED: {
    label: "弊社検品中",
    nextAction: "弊社で品質確認中です",
    owner: "弊社",
    color: "blue",
  },
  SUB_ORDER_REJECTED: {
    label: "作業準備中",
    nextAction: "作業開始までお待ちください",
    owner: "弊社",
    color: "blue",
  },
  SUB_DELIVERY_LATE: {
    label: "作業中",
    nextAction: "完了までお待ちください",
    owner: "弊社",
    color: "blue",
  },
  COMPANY_INSPECTION_ONGOING: {
    label: "弊社検品中",
    nextAction: "弊社で品質確認中です",
    owner: "弊社",
    color: "blue",
  },
  COMPANY_INSPECTION_COMPLETED: {
    label: "納品準備中",
    nextAction: "まもなく納品予定です",
    owner: "弊社",
    color: "blue",
  },
  COMPANY_DELIVERED: {
    label: "納品済み（要検収）",
    nextAction: "納品物をダウンロードし検収してください",
    owner: "お客様",
    color: "yellow",
  },
  COMPANY_REDO_REQUESTED: {
    label: "差し戻し対応中",
    nextAction: "修正版の納品をお待ちください",
    owner: "弊社",
    color: "yellow",
  },
  CUSTOMER_INSPECTION_ONGOING: {
    label: "検収中",
    nextAction: "検収完了 または 差し戻しを選択してください",
    owner: "お客様",
    color: "yellow",
  },
  CUSTOMER_INSPECTION_COMPLETED: {
    label: "納品完了",
    nextAction: "ご利用ありがとうございました",
    owner: "",
    color: "green",
  },
  CUSTOMER_DELIVERY_REJECTED: {
    label: "差し戻し対応中",
    nextAction: "修正版の納品をお待ちください",
    owner: "弊社",
    color: "yellow",
  },
};

// 顧客向けステータス色 → MUI Chip color / 背景色のマッピング
export const customerStatusColorMap: Record<
  CustomerStatusColor,
  { chipColor: "warning" | "info" | "success" | "error"; hex: string }
> = {
  yellow: { chipColor: "warning", hex: "#F9A825" },
  blue: { chipColor: "info", hex: "#1976D2" },
  green: { chipColor: "success", hex: "#2E7D32" },
  red: { chipColor: "error", hex: "#D32F2F" },
};

// 顧客ダッシュボードの絞り込み: 表示ラベル → 内部ステータス番号リストの逆引き。
// 1ラベルに複数の内部ステータスが対応するため、searchQuery.status（カンマ区切り番号）へ変換する。
// 「見積不可」は擬似ステータスのため専用キー "-3" で扱う（FailedEstimateSection 側で表示）。
export const customerStatusFilterOptions: {
  label: string;
  statusKeys: string[];
}[] = [
  { label: "見積完了（未発注）", statusKeys: ["CUSTOMER_CREATED"] },
  { label: "発注確定済み", statusKeys: ["CUSTOMER_ORDERED"] },
  {
    label: "作業中",
    statusKeys: [
      "COMPANY_ORDER_RECEIVED",
      "COMPANY_ASSIGNED",
      "COMPANY_DELIVERY_LATE",
      "SUB_ORDER_ACCEPTED",
      "SUB_INPROGRESS",
      "SUB_DELIVERED",
      "SUB_ORDER_REJECTED",
      "SUB_DELIVERY_LATE",
      "COMPANY_INSPECTION_ONGOING",
      "COMPANY_INSPECTION_COMPLETED",
    ],
  },
  { label: "納品済み（要検収）", statusKeys: ["COMPANY_DELIVERED"] },
  { label: "検収中", statusKeys: ["CUSTOMER_INSPECTION_ONGOING"] },
  { label: "納品完了", statusKeys: ["CUSTOMER_INSPECTION_COMPLETED"] },
  {
    label: "差し戻し対応中",
    statusKeys: ["COMPANY_REDO_REQUESTED", "CUSTOMER_DELIVERY_REJECTED"],
  },
  { label: "受注不可", statusKeys: ["COMPANY_ORDER_REJECTED"] },
];

// 顧客側ステッパーの段階ラベル（旧ラベル廃止済みの新表記に統一）。
// 段階判定は表示文字列の一致で行うため、作業系は全て「作業中」に集約する。
export const stepperStatusCustomer: Partial<Record<string, string>> = {
  CUSTOMER_CREATED: "見積完了",
  CUSTOMER_ORDERED: "発注確定",
  ...[
    "SUB_INPROGRESS",
    "COMPANY_ORDER_RECEIVED",
    "COMPANY_ASSIGNED",
    "COMPANY_DELIVERY_LATE",
    "SUB_ORDER_ACCEPTED",
    "SUB_DELIVERED",
    "SUB_ORDER_REJECTED",
    "SUB_DELIVERY_LATE",
    "COMPANY_INSPECTION_ONGOING",
    "COMPANY_INSPECTION_COMPLETED",
    "COMPANY_REDO_REQUESTED",
  ].reduce((acc, key) => ({ ...acc, [key]: "作業中" }), {}),
  COMPANY_DELIVERED: "納品済み",
  CUSTOMER_INSPECTION_ONGOING: "検収",
  CUSTOMER_INSPECTION_COMPLETED: "納品完了",
};

export const displayStatusCompany: Partial<Record<string, string>> = {
  CUSTOMER_CREATED: "顧客見積作成済み",
  CUSTOMER_ORDERED: "顧客発注済み受諾待ち",
  COMPANY_ORDER_RECEIVED: "自社受注済み",
  COMPANY_ASSIGNED: "自社発注済み受諾待ち",
  COMPANY_ORDER_REJECTED: "自社受注拒否済み",
  COMPANY_DELIVERY_LATE: "自社作業超過",
  SUB_ORDER_ACCEPTED: "外注先受注済み",
  SUB_INPROGRESS: "外注先作業中",
  SUB_DELIVERED: "外注先納品済み",
  SUB_ORDER_REJECTED: "外注先受注拒否済み",
  SUB_DELIVERY_LATE: "外注先作業超過",
  COMPANY_INSPECTION_ONGOING: "自社検品中",
  COMPANY_INSPECTION_COMPLETED: "自社検品済み",
  COMPANY_DELIVERED: "自社納品済み",
  COMPANY_REDO_REQUESTED: "自社やり直し依頼済み",
  CUSTOMER_INSPECTION_ONGOING: "顧客納品受入待ち",
  CUSTOMER_INSPECTION_COMPLETED: "顧客納品受入済み",
  CUSTOMER_DELIVERY_REJECTED: "顧客納品拒否済み",
};

export const stepperStatusCompany: Partial<Record<string, string>> = {
  CUSTOMER_CREATED: "顧客見積作成済み",
  CUSTOMER_ORDERED: "顧客発注済み受諾待ち",
  COMPANY_ORDER_RECEIVED: "自社受注済み",
  COMPANY_ASSIGNED: "自社発注済み受諾待ち",
  SUB_ORDER_ACCEPTED: "外注先受注済み",
  SUB_INPROGRESS: "外注先作業中",
  SUB_DELIVERED: "外注先納品済み",
  COMPANY_INSPECTION_ONGOING: "自社検品中",
  COMPANY_INSPECTION_COMPLETED: "自社検品済み",
  COMPANY_DELIVERED: "自社納品済み",
  CUSTOMER_INSPECTION_ONGOING: "顧客納品受入待ち",
  CUSTOMER_INSPECTION_COMPLETED: "顧客納品受入済み",
};

export const displayStatusSubcontractor: Partial<Record<string, string>> = {
  COMPANY_ASSIGNED: "受諾待ち",
  SUB_ORDER_ACCEPTED: "受注済み",
  SUB_INPROGRESS: "作業中",
  SUB_ORDER_REJECTED: "受注拒否済み",
  SUB_DELIVERED: "納品済み",
  COMPANY_REDO_REQUESTED: "やり直し依頼",
  SUB_DELIVERY_LATE: "外注先作業超過",
};

export const stepperStatusSubcontractor: Partial<Record<string, string>> = {
  COMPANY_ASSIGNED: "受諾待ち",
  SUB_ORDER_ACCEPTED: "受注済み",
  SUB_INPROGRESS: "作業中",
  SUB_DELIVERED: "納品済み",
};

export const displayStatusGanttCompany: any = {
  COMPANY_ORDER_RECEIVED: "受注済み",
  COMPANY_ASSIGNED: "外注先受諾待ち",
  SUB_ORDER_ACCEPTED: "外注先受注",
  SUB_INPROGRESS: "外注先作業中",
  SUB_ORDER_REJECTED: "	外注先受注拒否",
  SUB_DELIVERED: "外注先納品",
  COMPANY_INSPECTION_ONGOING: "自社検品中",
  COMPANY_INSPECTION_COMPLETED: "自社検品済み",
  COMPANY_REDO_REQUESTED: "やり直し依頼済み",
  CUSTOMER_INSPECTION_ONGOING: "顧客納品受入待ち",
  CUSTOMER_INSPECTION_COMPLETED: "納品完了",
  CUSTOMER_DELIVERY_REJECTED: "納品拒否",
};

export const displayStatusGanttSubcontractor: any = {
  SUB_ORDER_ACCEPTED: "受注",
  SUB_INPROGRESS: "作業中",
  SUB_ORDER_REJECTED: "受注拒否",
  SUB_DELIVERED: "納品",
  COMPANY_REDO_REQUESTED: "やり直し依頼",
};

export type PaginationData = {
  page: number;
  per_page: number;
  page_count: number;
  total_count: number;
};

export type PaginationResponse<T> = {
  data: T[];
  metadata: PaginationData;
};
